/** Same-origin Odoo bus WebSocket client (session cookie). */

export const NOTIFICATION_BUS_EVENT = 'notification/created';

/** Must match WebsocketConnectionHandler._VERSION in odoo/addons/bus. */
const ODOO_BUS_WORKER_VERSION = '19.0-2';

export interface BusNotificationEnvelope {
  id: number;
  message: {
    type: string;
    payload: unknown;
  };
}

type BusListener = (notifications: BusNotificationEnvelope[]) => void;

function websocketUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const params = new URLSearchParams({ version: ODOO_BUS_WORKER_VERSION });
  return `${protocol}//${window.location.host}/websocket?${params}`;
}

export class OdooBusClient {
  private ws: WebSocket | null = null;
  private listeners = new Set<BusListener>();
  private lastNotificationId = 0;
  private reconnectDelay = 1000;
  private reconnectTimer: number | null = null;
  private stopped = false;
  private intentionalClose = false;

  start(): void {
    this.stopped = false;
    this.intentionalClose = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.intentionalClose = true;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close(1000);
      this.ws = null;
    }
  }

  subscribe(listener: BusListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private connect(): void {
    if (this.stopped) {
      return;
    }
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    const ws = new WebSocket(websocketUrl());
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectDelay = 1000;
      // channels: [] — center channel is added server-side in ir.websocket.
      ws.send(
        JSON.stringify({
          event_name: 'subscribe',
          data: {
            channels: [],
            last: this.lastNotificationId,
          },
        })
      );
    };

    ws.onmessage = (event) => {
      let notifications: BusNotificationEnvelope[];
      try {
        notifications = JSON.parse(event.data as string);
      } catch {
        return;
      }
      if (!Array.isArray(notifications) || notifications.length === 0) {
        return;
      }
      const last = notifications[notifications.length - 1];
      if (typeof last?.id === 'number') {
        this.lastNotificationId = last.id;
      }
      for (const listener of this.listeners) {
        listener(notifications);
      }
    };

    ws.onclose = (event) => {
      this.ws = null;
      if (this.stopped || this.intentionalClose) {
        return;
      }
      if (event.code === 1000 && event.reason === 'OUTDATED_VERSION') {
        console.error(
          'Odoo bus websocket outdated; update ODOO_BUS_WORKER_VERSION'
        );
        return;
      }
      this.scheduleReconnect();
    };

    ws.onerror = () => {};
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null || this.stopped) {
      return;
    }
    const delay = this.reconnectDelay;
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30_000);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}
